import { Button, Datepicker } from '@acpaas-ui/react-components';
import moment from 'moment';
import 'moment/locale/nl';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { fetchReset } from '../actions/fetch.actions';
import AttachmentsList from '../components/attachments/AttachmentsList';
import { INPUT_STATES } from '../components/forms/forms.utils';
import TextArea from '../components/forms/TextArea';
import TextField from '../components/forms/TextField';
import EmailStakeholdersAction from '../components/stakeholders/EmailStakeholdersAction';
import { STAKEHOLDER_DOSSIER_PATHS } from '../constants/dossier.constants';
import { MODALS } from '../constants/modals.constants';
import { ATTACHMENT_TYPES, TOAST_TYPES } from '../constants/settings.constants';
import { TEMPLATE_CATEGORIES_SUFFIXES } from '../constants/templates.constants';
import StaticTable from '../containers/core/StaticTable';
import DossierTemplates from '../containers/dossier-templates/DossierTemplates';
import formValidators from '../form-validators';
import { useAttachmentCRUD } from '../hooks/useAttachmentCRUD.hook';
import { useForm } from '../hooks/useForm.hook';
import { useModal } from '../hooks/useModal.hook';
import { getCanSubmitContactMoment } from '../lib/contact.moments.utils';
import { attachmentBody, getAttachmentsForContactMoment } from '../lib/dossier.utils';
import { addStakeholderBody } from '../lib/stakeholder.utils';
import { getTemplateCategory } from '../lib/template.utils';
import StakeholderAddModal from '../modals/StakeholderAddModal';
import StakeholderDetailModal from '../modals/StakeholderDetailModal';
import { attachments as attachmentsBff } from '../services/bff.api';
import { CONTACTMOMENT_STAKEHOLDERS_RESOURCE } from '../resources/contactmoments.stakeholders.resource';
import { DOSSIER_CONTACTMOMENTS_RESOURCE } from '../resources/dossier.contactmoments.resource';
import contactMomentsService from '../services/api/contactmoments.service';
import AttachmentDetailsModal from './AttachmentDetailsModal';
import AttachmentUploadModal from './AttachmentUploadModal';
import Modal from './Modal';
import ModalTitle from './ModalTitle';
import toastService from '../services/toasts.service';

const DEFAULT_FORM = {
  date: moment(),
  startTime: '',
  endTime: '',
  duration: '',
  subject: '',
  description: '',
  involved: [],
  attachments: []
};

const ContactMomentAddModal = ({
  dossierId,
  dossierType,
  dossierSchools,
  attachments,
  loadingAttachments,
  onAttachmentsRefetch,
  isOpen,
  onAdded,
  onClose
}) => {
  const dispatch = useDispatch();
  const modalContentRef = useRef(null);
  const { data, isSubmitting, input, load, submitStart, submitOk, submitError } = useForm({ ...DEFAULT_FORM });
  const [validation, setValidation] = useState({});
  const stakeholderAddModal = useModal(MODALS.STAKEHOLDER_ADD_MODAL);
  const stakeholderDetailModal = useModal(MODALS.STAKEHOLDER_DETAIL_MODAL);
  const [stakeholderDetailModalData, setStakeholderDetailModalData] = useState({});
  const attachmentCRUD = useAttachmentCRUD(dossierId);
  const [tempAttachments, setTempAttachments] = useState([]);

  const validators = {
    date: formValidators.date.dateTextField,
    startTime: formValidators.time.hourMinuteField,
    endTime: formValidators.time.hourMinuteField
  };

  const handleClose = () => {
    load({ ...DEFAULT_FORM });
    dispatch(fetchReset(CONTACTMOMENT_STAKEHOLDERS_RESOURCE));
    onClose();
  };

  const addAttachmentsOnConfirmSuccess = (contactMomentId) =>
    Promise.allSettled(
      tempAttachments.map((att) =>
        attachmentsBff
          .uploadForDossier(dossierId, attachmentBody(contactMomentId, ATTACHMENT_TYPES.CONTACT_MOMENT, att))
          .then((resp) => {
            if (resp.status !== 200) {
              toastService.showToast(TOAST_TYPES.ERROR, `Kan bijlage ${att.name || att.fileName || ''} niet uploaden`);
            }
          })
      )
    );

  const handleConfirm = () => {
    submitStart();
    contactMomentsService.add(dossierId, data, {
      onSuccess: async (contactMoment) => {
        addAttachmentsOnConfirmSuccess(contactMoment.id).then(() => {
          onAttachmentsRefetch();
          submitOk();
          onAdded();
          dispatch(fetchReset(DOSSIER_CONTACTMOMENTS_RESOURCE));
        });
      },
      onError: (message) => {
        submitError(message);
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = modalContentRef.current.scrollHeight;
        }
      }
    });
  };

  const handleEnter = (event) => {
    if (event.code === 'Enter' && getCanSubmitContactMoment(data, validation)) {
      handleConfirm();
    }
  };

  const handleInput = (value, field) => {
    if (validators[field]) {
      setValidation({ ...validation, [field]: validators[field](value, false) });
    }
    input(value, field);
  };

  const handleStakeholderOpenClick = (data = {}) => {
    setStakeholderDetailModalData(data);
    stakeholderDetailModal.onOpen();
  };

  const handleStakeholderAdded = (value) => {
    const stakeholderExists = data?.involved.find((el) => el.id === value.id);
    if (!stakeholderExists) {
      input([...data?.involved, addStakeholderBody(value)], 'involved');
      dispatch(fetchReset(CONTACTMOMENT_STAKEHOLDERS_RESOURCE));
    }
    stakeholderAddModal.onClose();
  };

  const handleStakeholderUpdated = (value) => {
    const updatedInvolved = data?.involved?.map((i) => {
      if (i.id === value.id) {
        return { ...value };
      }
      return i;
    });
    input(updatedInvolved, 'involved');
    dispatch(fetchReset(CONTACTMOMENT_STAKEHOLDERS_RESOURCE));
    stakeholderDetailModal.onClose();
  };

  const handleStakeholderDeleted = (value) => {
    const newValue = data?.involved.filter((el) => el.id !== value.id);
    input(newValue, 'involved');
    dispatch(fetchReset(CONTACTMOMENT_STAKEHOLDERS_RESOURCE));
    stakeholderDetailModal.onClose();
  };

  const handleAddAttachment = (attachment) => {
    setTempAttachments([
      ...tempAttachments,
      {
        ...attachment,
        dossierId,
        type: attachmentCRUD.openedAttachmentType,
        fileName: attachment.attachment.name,
        id: uuid()
      }
    ]);
  };

  const handleUpdateAttachment = (attachmentId, changes) => {
    setTempAttachments(tempAttachments.map((att) => (att.id === attachmentId ? { ...att, ...changes } : att)));
  };

  const handleDeleteAttachment = (attachment) => {
    setTempAttachments(tempAttachments.filter((att) => att.id !== attachment.id));
    attachmentCRUD.detailsModal.onClose();
  };

  return (
    <>
      <Modal
        ref={modalContentRef}
        className="spo-o-add-contactmoment-modal"
        onClose={handleClose}
        onConfirm={handleConfirm}
        canConfirm={getCanSubmitContactMoment(data, validation)}
        isSubmitting={isSubmitting}
        onConfirmLabel="Toevoegen"
        onConfirmIcon="ai-check-1"
        title={<ModalTitle title="Contactmoment toevoegen" />}
        isOpen={isOpen}
        width={1000}
      >
        <div className="row u-margin-bottom-sm">
          <div className="col-xs-12 col-md-4">
            <Datepicker
              required
              format={'DD/MM/YYYY'}
              mask={'99/99/9999'}
              label="Datum contactmoment"
              activeDate={moment(data?.date, 'DD/MM/YYYY').format('DD/MM/YYYY')}
              description={validation.date ? <span className="u-text-danger">{validation.date}</span> : ''}
              // state={INPUT_STATES.DEFAULT}
              onChange={(value) => handleInput(value, 'date')}
              disabled={isSubmitting}
            />
          </div>
          <div className="col-xs-12 col-md-3">
            <TextField
              label="Starttijd"
              mask="99:99"
              placeholder="__:__"
              value={data?.startTime}
              name={'startTime'}
              state={INPUT_STATES.DEFAULT}
              onKeyDown={handleEnter}
              onChange={(value) => handleInput(value, 'startTime')}
              disabled={isSubmitting}
              validationError={validation.startTime}
              hasError={!!validation.startTime}
            />
          </div>
          <div className="col-xs-12 col-md-3">
            <TextField
              label="Eindtijd"
              mask="99:99"
              placeholder="__:__"
              value={data?.endTime}
              name={'endTime'}
              state={INPUT_STATES.DEFAULT}
              onKeyDown={handleEnter}
              onChange={(value) => handleInput(value, 'endTime')}
              disabled={isSubmitting}
              validationError={validation.endTime}
              hasError={!!validation.endTime}
            />
          </div>
        </div>
        <div className="row u-margin-bottom-md">
          <div className="col-xs-12 col-md-12">
            <TextField
              label="Onderwerp"
              value={data?.subject}
              name={'subject'}
              state={INPUT_STATES.DEFAULT}
              onKeyDown={handleEnter}
              onChange={(value) => handleInput(value, 'subject')}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-12">
            <StaticTable
              resource={CONTACTMOMENT_STAKEHOLDERS_RESOURCE}
              onRowClick={handleStakeholderOpenClick}
              data={data?.involved}
              actions={[
                <EmailStakeholdersAction
                  key="stakeholder-email"
                  className="u-margin-right-sm"
                  stakeholders={data?.involved}
                />,
                <Button
                  key="stakeholder-add"
                  size="small"
                  className="u-margin-right-sm"
                  iconRight="ai-add"
                  onClick={stakeholderAddModal.onOpen}
                >
                  Betrokkene toevoegen
                </Button>
              ]}
              responsive
            />
          </div>
        </div>
        <div className="row u-margin-bottom-sm">
          <div className="col-xs-12 col-md-12">
            <TextArea
              label="Nota's"
              value={data?.description}
              name={'description'}
              state={INPUT_STATES.DEFAULT}
              onKeyDown={handleEnter}
              onChange={(value) => handleInput(value, 'description')}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="row u-margin-bottom-sm">
          <div className="col-xs-12 col-md-12">
            <AttachmentsList
              className="u-margin-bottom-lg"
              title="Bestanden"
              attachments={getAttachmentsForContactMoment(data, [...attachments, ...tempAttachments])}
              loading={loadingAttachments}
              noAttachmentsText="Er zijn momenteel nog geen bestanden toegevoegd."
              onOpen={attachmentCRUD.onAttachmentOpen}
              onUpload={() => {
                attachmentCRUD.onAttachmentAddOpen(data.id, ATTACHMENT_TYPES.CONTACT_MOMENT);
              }}
              templates={
                <DossierTemplates
                  category={getTemplateCategory(dossierType, TEMPLATE_CATEGORIES_SUFFIXES.CONTACTMOMENT)}
                />
              }
            />
          </div>
        </div>
      </Modal>
      {stakeholderAddModal.isOpen && (
        <StakeholderAddModal
          {...stakeholderAddModal}
          dossierId={dossierId}
          dossierSchools={dossierSchools}
          onUpdated={handleStakeholderAdded}
          dossierPath={STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD}
        />
      )}
      {stakeholderDetailModal.isOpen && (
        <StakeholderDetailModal
          {...stakeholderDetailModal}
          data={stakeholderDetailModalData}
          onUpdated={handleStakeholderUpdated}
          onDeleted={handleStakeholderDeleted}
          dossierPath={STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD}
        />
      )}
      {attachmentCRUD.uploadModal.isOpen && (
        <AttachmentUploadModal {...attachmentCRUD.uploadModal} onConfirm={handleAddAttachment} inline />
      )}
      {attachmentCRUD.detailsModal.isOpen && (
        <AttachmentDetailsModal
          {...attachmentCRUD.detailsModal}
          dossierId={dossierId}
          form={attachmentCRUD.attachmentDetailForm}
          onUpdate={handleUpdateAttachment}
          inlineEdit
          onDelete={handleDeleteAttachment}
        />
      )}
    </>
  );
};

export default ContactMomentAddModal;
